import {
	EventAggregator,
	Subscription as EventSubscription,
} from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	BindingEngine,
	bindingMode,
	computedFrom,
	Container,
	customElement,
	TaskQueue,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import {
	FluentRuleCustomizer,
	FluentRules,
	ValidationRules,
} from 'aurelia-validation';
import { isEqual } from 'lodash-es';

import { Form } from '~/components/form/form';
import { DateFormatValueConverter } from '~/converters/date-format';
import { convertInputMask } from '~/inputmasks/input-mask-converter';
import { DataType, DataTypeEnum } from '~/models/data-type';
import { ValidationRuleName } from '~/models/field-validation';
import { FieldModel, Modifiable } from '~/models/form-model';
import { Logger } from '~/utils/logger';
import {
	CustomValidationMessage,
	CustomValidationRuleName,
} from '~/validation/custom-validation-rules';

import Inputmask from 'inputmask';
import debounce from 'lodash-es/debounce';
import FormModelUtils from '~/utils/form-model-utils';

/**
 * Field types. This will determine what will be rendered in `field.html`
 */
enum FieldType {
	BLANK = 'BLANK',
	CHECKBOX = 'CHECKBOX',
	DATEPICKER = 'DATEPICKER',
	NUMBER = 'NUMBER',
	SEARCH = 'SEARCH',
	SELECT = 'SELECT',
	MULTISELECT = 'MULTISELECT',
	TEXT = 'TEXT',
	SUBURB = 'SUBURB',
	STATE = 'STATE',
	COUNTRY = 'COUNTRY',
	POSTCODE = 'POSTCODE',
}

// NOTE: Jake: 2019-05-08
// Unused. Should be deleted if not necessary.
/* enum FieldTextStyle {
	// NOTE(Jake): 2018-10-04 - WEBUI-1054
	// Used to be `DEFAULT = 'text-default'`
	// However this made a field description be grey AND have opacity so it looked
	// more faded than it should.
	DEFAULT = '',
	DANGER = 'text-danger',
}*/

export interface FieldEventData {
	model: FieldModel;
	/**
	 * This is true if the field was editable at the time the event was fired. It would be false if the field was
	 * modifiable but belonged to a form that was in view mode (as apposed to edit mode).
	 */
	isFieldReadonly: boolean;

	/**
	 * This is the Field custom element object itself
	 */
	fieldViewModel: Field;
}

export interface EndFieldEventData extends FieldEventData {
	/**
	 * The element clicked that caused the blur() event to fire on this field.
	 */
	relatedTarget: HTMLElement | undefined;
}

const classes = {
	'a-field--hidden': 'a-field--hidden',
};

/**
 * The base Field class
 */
@autoinject
@customElement('a-field')
export class Field {
	/**
	 * Field Events
	 */
	public static readonly Events = {
		FieldModelValueChanged: 'Field.FieldModelValueChanged',
		EndFieldTriggered: 'Field.EndFieldTriggered',
	};
	/**
	 * Field type
	 */
	public static readonly TYPE = FieldType;
	/**
	 * Generated unique field ID; prefixed by `parentId`, if available
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	protected fieldId: string = '';
	/**
	 * Parent ID, declared on the parent `Form` component
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	protected parentId: string;
	/**
	 * Initialize with an empty value
	 */
	@bindable protected initEmpty: boolean = false;
	/**
	 * Override field label, which usually comes from thee `fieldModel`
	 */
	@bindable protected label: string = '';
	/**
	 * Used to store label overrides received by this component
	 */
	private labelOverride: string;
	/**
	 * The space that the label takes up is removed if this is true (it also removes the label itself).
	 */
	@bindable protected isLabelHidden: boolean = false;
	/**
	 * Field placeholder
	 */
	@bindable protected placeholder: string = '';
	/**
	 * Field validation message
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	public validationMessage: string = '';

	private inputmaskMessage: string = '';

	@bindable private isContextMenuDisabled: boolean = false;

	/**
	 * Input masks are not supported for some field types, setting this to true explicitly disables
	 * input mask initialisation
	 */
	protected isDisableInputMasks = false;

	/**
	 * Override readonly property on the field input
	 */
	@bindable protected isReadonly: boolean = true;

	/**
	 * Force readonly mode on a field.
	 * This flag was introduced for the <address> component so that
	 * fields can be disabled when a user should use QAS.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	protected forceReadonly: boolean = false;

	/**
	 * Override disabled
	 */
	@bindable protected disabled: boolean = false;

	/**
	 * NOTE: Jake: 2019-05-08
	 * Unused. Should not be able to set a fixed width on a field.
	 * Override the form's width. This takes a CSS value to be set on the `width` property
	 */
	// @bindable protected width: string;

	/**
	 * A list of event subscriptions to automatically dispose() when component unbind() is called.
	 */
	protected eventSubscriptions: EventSubscription[] = [];

	/**
	 * DEPRECATED.
	 * This is only used by the multiselect.ts component and should not be used by
	 * anything else.
	 */
	protected isMultiselectField: boolean = false;

	/**
	 * Supplementary input value description
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
		changeHandler: 'descriptionChanged',
	})
	protected description: string = '';

	/**
	 * NOTE: Jake: 2019-05-08
	 * Unused. Cannot find "text-style" used in any template.
	 * Supplementary input value description
	 */
	// protected textStyle: FieldTextStyle = FieldTextStyle.DEFAULT;

	/**
	 * Basically `this.element`
	 */
	protected fieldElem: HTMLElement;
	/**
	 * The triangle error icon element, a-field__error-icon
	 */
	protected errorIconElem?: HTMLElement;
	/**
	 * The field input element, ie '<input>' or '<select>'
	 */
	protected refInputElem?: HTMLInputElement | HTMLSelectElement;

	/**
	 * Note(York): This bindable property should be placed before fieldModel because fieldModel use it when initializing
	 * validation rules. When this file is imported and loaded, changeHandlers of each bindable property are
	 * called in the order in which they are defined if there are changes.
	 *
	 * Validation rules, given custom validation rules' name defined in `custom-validation-rules.ts`
	 * Can be set values or used in custom element like this
	 * <a-field ...
	 * 		validation-rules.one-time="['RULE1', 'RULE2', ...]"
	 * ... >
	 * If you define a rule in CustomValidationRuleName enum, you can do this. This is just for code readibility, telling
	 * others I have a rule defined. You need to import a hook in html as below
	 * <require from="validation/custom-validation-rule-enum"></require>
	 * <a-field ...
	 * 		validation-rules.one-time="[ValidationRuleEnum.RULE1, ValidationRuleEnum.RULE2, ...]"
	 * ... >
	 * see `validation/custom-validation-rule-enum`
	 */
	@bindable({ defaultBindingMode: bindingMode.oneTime })
	protected validationRules: CustomValidationRuleName[] = [];

	/**
	 * The `FieldModel` that came from the `FormModel`. It will be undefined in the customisation editor.
	 */
	@bindable({
		defaultBindingMode: bindingMode.twoWay,
		changeHandler: 'fieldModelChanged',
	})
	public fieldModel?: FieldModel;

	/**
	 * The value of the field. Changes to this value should be synced with "fieldModel.value".
	 */
	@bindable({
		defaultBindingMode: bindingMode.twoWay,
		changeHandler: 'valueChanged',
	})
	public value: any;

	/**
	 * Input type (`<input type="<text | number>"`) determined by the fieldModel
	 */
	protected inputType: 'text' | 'number' = 'text';

	/**
	 * Form panel: fieldDataType is from fieldModel
	 * Filter panel: fielDataType is from binding which is from tableColumnInfo.dataType: DataType
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	protected fieldDataType: DataType;

	/**
	 * Field type used by the template, `field.html`
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private readonly _TYPE = FieldType;

	protected bindingEngine: BindingEngine;
	protected taskQueue: TaskQueue;
	protected i18n: I18N;

	constructor(
		protected fieldType: FieldType,
		public element: Element,
		protected parentForm: Form,
		readonly eventAggregator: EventAggregator,
	) {
		// TODO: WEBUI-1131 - Constructor DI or Get from container
		this.bindingEngine = Container.instance.get(BindingEngine);
		this.taskQueue = Container.instance.get(TaskQueue);
		this.i18n = Container.instance.get(I18N);

		// fieldModelChanged uses this.fieldElem and can be called before the field is attached. Therefore,
		// this.fieldElem should be set here.
		this.fieldElem = this.element as HTMLElement;

		// sometimes fieldModelChanged gets called before refInputElem is rendered and thus fails to apply masks.
		// e.g. when fields are rendered dynamically in correspondence register forms
		this.bindingEngine
			.propertyObserver(this, 'refInputElem')
			.subscribe((n, o) => this.inputElemChanged(n, o));
	}

	/**
	 * Base attached() method
	 */
	protected attached(): void {
		this.applyValidationRules();
		this.applyEndField();
		this.applyInputmask();
	}

	protected detached() {
		if (this.refInputElem && this.fieldModel && this.fieldModel.endField) {
			this.refInputElem.removeEventListener(
				'blur',
				this.triggerEndFieldListener,
			);
		}
	}

	protected unbind(): void {
		for (const subscription of this.eventSubscriptions) {
			subscription.dispose();
		}
		this.eventSubscriptions = [];
	}

	/**
	 * Clear the field if it's modifiable
	 */
	public clear() {
		if (this.fieldModel && this.fieldModel.modifiable === Modifiable.TRUE) {
			Logger.log('Clearing value for field:', this.fieldModel.name);
			this.fieldModel.value = '';
		} else {
			Logger.log(
				'Unable to clear the value for',
				this.fieldModel || this.fieldModel.name,
			);
		}
	}

	/**
	 * Calculate field properties
	 */
	protected fieldModelChanged(fieldModel: FieldModel) {
		if (fieldModel) {
			// If `fieldModel.visible` is false, hide this field
			this.setFieldVisible(fieldModel.visible);
			// Note: York
			// inputmask might be set based on last field model value, especially when the inputmask set a
			// scale. It will affect input value from this.value
			// so clear input mask or apply new inputmask before `this.value = fieldModel.value`
			this.applyInputmask();

			// Core properties
			if (this.value !== fieldModel.value) {
				this.value = fieldModel.value;
			}
			if (
				fieldModel.dataType &&
				fieldModel.dataType.name === 'String' &&
				this.value === undefined
			) {
				// NOTE: Jake: 2019-01-02 - WEBUI-1148
				// We set `undefined` values to '' if the data type is
				// a string. This is to stop values being "undefined" in
				// text fields.
				this.value = '';
			}
			if (this.labelOverride === undefined) {
				// If we have received an overriding label but have not set the variable yet, do it now.
				// This will not be set up in subsequent renders.
				// Empty strings are assumed to not be override values and will be set as null.
				this.labelOverride = this.label?.trim() || null;
			}

			this.label = this.labelOverride || fieldModel.label?.trim();

			// multi-select filter fields bind a field model but do not have a parent form
			if (!this.isMultiselectField) {
				this.parentId = this.parentForm.parentId;
			}
			this.fieldId =
				(this.parentId ? this.parentId + '-' : '') + fieldModel.name;

			// Description
			this.calculateAndSetDescriptionFromFieldModel(fieldModel);

			this.fieldDataType = fieldModel.dataType || this.fieldDataType;

			this.bindingEngine
				.propertyObserver(this.fieldModel, 'value')
				.subscribe(this.fieldModelValueChanged);
		}

		this.applyValidationRules();
	}

	protected calculateAndSetDescriptionFromFieldModel(fieldModel: FieldModel) {
		this.description = this.getDescriptionFromFieldModel(fieldModel);
	}

	/**
	 * Get the description for this field, giving precedence to the 'fieldDescription' value
	 */
	private getDescriptionFromFieldModel(fieldModel: FieldModel): string {
		let description = '';

		if (fieldModel.codeList) {
			for (const codeAndDescription of fieldModel.codeList) {
				if (
					codeAndDescription &&
					codeAndDescription.code === fieldModel.value
				) {
					description = codeAndDescription.description;
					break;
				}
			}
		}

		if (fieldModel.fieldDescription) {
			description = fieldModel.fieldDescription.trim();
		}

		return description;
	}

	/**
	 * Check if form field is readonly
	 * if fieldModel does not exist, form field is readonly by default
	 * if fieldModel exists and its Modifiable.TRUE or Modifiable.INSERT_ONLY, form field is not readonly
	 */
	@computedFrom(
		'fieldModel',
		'fieldModel.modifiable',
		'isReadonly',
		'forceReadonly',
	)
	get isFieldReadonly(): boolean {
		if (this.forceReadonly) {
			return true;
		}
		const canModify =
			this.fieldModel &&
			(this.fieldModel.modifiable === Modifiable.TRUE ||
				this.fieldModel.modifiable === Modifiable.INSERT_ONLY);
		return !canModify && this.isReadonly;
	}

	/**
	 * Get inputmask from field model
	 */
	@computedFrom('fieldModel', 'fieldModel.inputMask')
	get inputmaskOptions(): inputmask.InputmaskOptions | undefined {
		if (this.fieldModel && this.fieldModel.inputMask) {
			return {
				autoUnmask: false,
				mask: convertInputMask(this.fieldModel.inputMask),
				onKeyValidation: this.onInputmaskKeyValidation,
				oncomplete: this.clearInputmaskMessage,
			};
		}
		return undefined;
	}

	private readonly onInputmaskKeyValidation = (
		key: number,
		resultOrPos: boolean | { pos: number },
		opts?: any,
	): void => {
		if (resultOrPos === false) {
			this.inputmaskMessage = this.i18n.tr('errors.inputmask-error', {
				inputmask: this.fieldModel.inputMask,
			});
			this.clearInputmaskMessageDebounced();
		} else {
			this.clearInputmaskMessage();
		}
	};

	private readonly clearInputmaskMessage = (): void => {
		this.inputmaskMessage = '';
	};

	/**
	 * Clear input mask message after 4 seconds, function is debounced
	 */
	private readonly clearInputmaskMessageDebounced = debounce(
		this.clearInputmaskMessage,
		4000,
	);

	/**
	 * Fires when "fieldModel.value" has been modified
	 */
	private fieldModelValueChanged = (newValue, oldValue): void => {
		if (this.value !== newValue) {
			this.value = newValue;
		}
	};

	/**
	 * Default handler for changed value. Fires when record is changed
	 */
	protected valueChanged(value, oldValue) {
		if (!this.fieldModel) {
			return;
		}

		if (this.fieldModel.value !== value) {
			this.fieldModel.value = value;
		}
	}

	/**
	 * Default handler for changed description. Fires when record is changed
	 */
	protected descriptionChanged(value, oldValue) {
		if (!this.fieldModel) {
			return;
		}

		if (this.fieldModel.fieldDescription !== value) {
			this.fieldModel.fieldDescription = value;
		}
	}

	/**
	 * Field title
	 */
	private getFieldTemplate() {
		return this.fieldType.toLowerCase() + '-template';
	}

	/**
	 * Compute the display value.
	 * Used when the field is in non-edit mode.
	 */
	@computedFrom('value')
	get displayValue() {
		return this.value;
	}

	/**
	 * Field title
	 */
	@computedFrom('fieldModel', 'fieldModel.name', 'label')
	get fieldTitle() {
		return (this.fieldModel && this.fieldModel.name) || this.label || '';
	}

	private applyValidationRules() {
		let fluentRulesCustomizer: FluentRuleCustomizer<any, any>;

		// 1. required rule
		const fluentRulesOrCustomizer = ValidationRules.ensure(
			(f: Field) => f.value,
		);
		if (this.fieldModel && this.fieldModel.required) {
			fluentRulesCustomizer = fluentRulesOrCustomizer.required();
		}

		if (fluentRulesOrCustomizer instanceof FluentRules) {
			// TODO: York - add a noop rule to FluentRules API
			fluentRulesCustomizer = fluentRulesOrCustomizer.satisfies(
				() => true,
			);
		}

		// 2. length validation
		this.setFieldValidation(this, fluentRulesCustomizer);
		// 3. custom field validations
		if (this.fieldModel && this.fieldModel.fieldValidationList) {
			for (const validation of this.fieldModel.fieldValidationList) {
				// copy from acurity-field
				if (
					validation.ruleName === ValidationRuleName.DATE_AFTER ||
					validation.ruleName === ValidationRuleName.DATE_BEFORE
				) {
					validation.controlValue =
						DateFormatValueConverter.toLocaleDate(
							validation.controlValue,
						);
				}
				fluentRulesCustomizer.satisfiesRule(
					validation.ruleName,
					validation,
				);
			}
		}

		// 4. rules from element binding
		this.validationRules.forEach((rule) => {
			if (rule in FluentRules.customRules) {
				fluentRulesCustomizer.satisfiesRule(rule);
			} else {
				Logger.warning(`unknown rule: ${rule}`);
			}
		});

		// 5. inherit field validation
		// this.setFieldModelValidation(fluentRulesCustomizer);

		// 6. apply rules to fieldModel object
		fluentRulesCustomizer.on(this);
	}

	// TODO: York - make this a interface/decorator/bindable property which can be plugged in and be field type irrelevant
	// protected setFieldModelValidation(fluentRulesCustomizer: FluentRuleCustomizer<FieldModel, any>) {
	// Logger.debug('basic');
	// }

	protected setFieldValidation(
		field: Field,
		fluentRulesCustomizer: FluentRuleCustomizer<Field, any>,
	) {
		if (!field.fieldModel || !field.fieldModel.dataType) {
			// Ignore if no data type (as it's an optional parameter on FieldModel)
			return;
		}

		const dataType: DataTypeEnum = field.fieldModel.dataType.name;
		// If string field is user defined, frontend should not validate for length
		if (
			field.fieldModel.name.startsWith(
				FormModelUtils.CUSTOMISATION_LEVEL_USER_DEFINED_PREFIX,
			) &&
			dataType === DataTypeEnum.String
		) {
			return;
		}

		switch (dataType) {
			case 'String':
				fluentRulesCustomizer
					.maxLength(field.fieldModel.length - 1)
					.withMessage(CustomValidationMessage.EXCEED_MAX_LENGTH);
				break;
			case 'Character':
				fluentRulesCustomizer
					.maxLength(field.fieldModel.length)
					.withMessage(CustomValidationMessage.EXCEED_MAX_LENGTH);
				break;
			// case Integer, Long, BigDecimal and Currency are moved to number.ts
		}
	}

	protected shouldSkipEndFieldInBlur(
		relatedTarget: HTMLElement | undefined,
	): boolean {
		return false;
	}

	protected onBlurMaybeTriggerEndFieldEvent(e: FocusEvent) {
		const relatedTarget =
			e.relatedTarget && e.relatedTarget instanceof HTMLElement
				? e.relatedTarget
				: undefined;
		if (this.shouldSkipEndFieldInBlur(relatedTarget)) {
			return;
		}
		this.triggerEndFieldEvent(relatedTarget);
	}

	protected triggerEndFieldEvent(
		relatedTarget: HTMLElement | undefined,
	): void {
		const eventId = Field.Events.EndFieldTriggered;
		Logger.log('Publishing event:', eventId);
		const data: EndFieldEventData = {
			model: this.fieldModel,
			isFieldReadonly: this.isFieldReadonly,
			fieldViewModel: this,
			relatedTarget,
		};
		this.eventAggregator.publish(eventId, data);
	}

	/**
	 * Add a blur listener to notify an End Field event should occur..
	 * End Field processing is done whenever we leave a field, even if the value has not changed.
	 * This is a business requirement - see WEBUI-3012
	 */
	private applyEndField() {
		if (this.refInputElem && this.fieldModel && this.fieldModel.endField) {
			this.refInputElem.addEventListener(
				'blur',
				this.triggerEndFieldListener,
			);
		}
	}

	/**
	 * The trigger end field event listener needs to be an external named function otherwise the
	 * listeners accumulate every time inputElemChanged fires (every time the field enters edit mode)
	 */
	private readonly triggerEndFieldListener = (event: FocusEvent): void =>
		this.onBlurMaybeTriggerEndFieldEvent(event);

	/**
	 * Note: York
	 * inputMask is from `fieldModel.inputMask` and applies on input element which is a ref
	 * This method is usually called in fieldModelChanged and fieldModelChanged could be called
	 * before attached, when refInputElem is still empty. Therefore, we need to observe refInputElem
	 * changes by bindingEngine.propertyObserver.
	 */
	protected applyInputmask() {
		if (
			!this.isDisableInputMasks &&
			this.refInputElem instanceof HTMLInputElement && // includes null check
			this.inputmaskOptions
		) {
			// NOTE: York - WEBUI-2392
			// If input mask is already applied on the element and inputmask is not changed, don't re-initialise it
			const inputmaskProp = (this.refInputElem as any).inputmask;
			const inputmaskOptions = this.inputmaskOptions; // in case computation of get property run every time it is referred below
			if (
				inputmaskProp &&
				inputmaskProp.userOptions &&
				isEqual(inputmaskProp.userOptions, inputmaskOptions)
			) {
				return;
			}

			Logger.debug(
				'Apply inputmask on input',
				inputmaskOptions,
				this.refInputElem,
			);
			Inputmask(inputmaskOptions).mask(this.refInputElem);
			// clear input mask message when blur
			this.refInputElem.addEventListener(
				'blur',
				this.clearInputmaskMessage,
			);
			this.refInputElem.addEventListener(
				'wheel',
				this.clearInputmaskMessage,
			);

			// Inputmask library performance issue: it takes about 1600ms to apply inputmask on 100 input elements
			// ```javascript
			// 	var t0 = performance.now();
			// 	for (var i = 0; i < 100; i++) Inputmask(this.inputMask).mask(this.refInputElem);
			// 	var t1 = performance.now();
			// 	Logger.log("Duratiion " + Math.round(t1 - t0) + " milliseconds.")
			// ```
			// A solution is to initialize inputmask only when element is focus.
			// this.refInputElem.addEventListener('focus', () => {
			// 	Inputmask(this.inputMask).mask(this.refInputElem);
			// });
		}
	}

	protected clearInputmask() {
		if (this.refInputElem) {
			Inputmask.remove(this.refInputElem);
		}
	}

	protected inputElemChanged(newInputElem, oldInputElem) {
		this.applyEndField();
		this.applyInputmask();
	}

	private setFieldVisible(visible: boolean): void {
		// NOTE: Jake: 2019-04-11 - WEBUI-1309
		// I changed from 'aurelia-hide' to 'a-field--hidden' to avoid
		// coupling our styling to the Aurelia framework.
		this.element.classList.toggle(classes['a-field--hidden'], !visible);
	}
}
